import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ComponentheaderService } from 'src/app/services/componentheader.service';
import { ClassmanagementService } from 'src/app/services/classmanagement.service';
import { ModulemanagementService } from 'src/app/services/modules/modulemanagement.service';
import { abridgedclass, moduledetails } from 'src/app/models/module/moduledetails';
import { FlashcardCard, LessonGameEdit, LessonGameKind, MatchPairEdit, SequenceStepEdit } from 'src/app/models/module/lessongame.model';

@Component({
  selector: 'app-lesson-game-editor',
  templateUrl: './lesson-game-editor.component.html',
  styleUrls: ['./lesson-game-editor.component.css']
})
export class LessonGameEditorComponent implements OnInit {
  classid: number;
  moduleinfo: moduledetails = new moduledetails();
  lessondetails: abridgedclass;
  games: LessonGameEdit[] = [];
  editing: LessonGameEdit = null;
  saving = false;
  gameToDelete: LessonGameEdit = null;

  constructor(
    private classmanager: ClassmanagementService,
    private moduleservice: ModulemanagementService,
    private headerinfo: ComponentheaderService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService
  ) { }

  async ngOnInit() {
    this.headerinfo.setHeaderDisplay('Loading...', [], []);
    if (this.route.snapshot.queryParams.classid > 0) {
      this.classid = parseInt(this.route.snapshot.queryParams.classid, 10);
    }
    await this.loadBreadcrumb();
    await this.loadGames();
  }

  async loadBreadcrumb() {
    this.lessondetails = await this.classmanager.getClassDetailsAbridged(this.classid).toPromise();
    this.moduleinfo = await this.classmanager.getClassModuleDetails(this.classid).toPromise();
    this.headerinfo.setHeaderDisplay('Lesson Activities', [
      { displaytext: 'School', route: `/user/schoolhome` },
      { displaytext: 'Course', route: `/user/moduledetails`, params: { moduleID: this.moduleinfo.id } },
      { displaytext: 'Lesson', route: `/user/lesson`, params: { classid: this.classid } },
      { displaytext: 'Lesson Activities', route: this.route.snapshot.url.join('/'), params: this.route.snapshot.queryParams }
    ], []);
  }

  async loadGames() {
    this.games = await this.classmanager.getLessonGamesForEdit(this.classid).toPromise();
  }

  newGame(kind: LessonGameKind) {
    const game = new LessonGameEdit();
    game.schoolModuleClassID = this.classid;
    game.gameType = kind;
    game.page = this.games.length + 1;
    game.pairs = [new MatchPairEdit(), new MatchPairEdit()];
    game.steps = [new SequenceStepEdit(), new SequenceStepEdit()];
    game.cards = [new FlashcardCard(), new FlashcardCard()];
    this.editing = game;
  }

  editGame(game: LessonGameEdit) {
    // work on a copy so cancelling doesn't mutate the list
    this.editing = JSON.parse(JSON.stringify(game));
    if (!this.editing.pairs || this.editing.pairs.length === 0) { this.editing.pairs = [new MatchPairEdit(), new MatchPairEdit()]; }
    if (!this.editing.steps || this.editing.steps.length === 0) { this.editing.steps = [new SequenceStepEdit(), new SequenceStepEdit()]; }
    if (!this.editing.cards || this.editing.cards.length === 0) { this.editing.cards = [new FlashcardCard(), new FlashcardCard()]; }
  }

  cancelEdit() {
    this.editing = null;
  }

  addPair() {
    this.editing.pairs.push(new MatchPairEdit());
  }

  removePair(index: number) {
    this.editing.pairs.splice(index, 1);
  }

  addStep() {
    this.editing.steps.push(new SequenceStepEdit());
  }

  removeStep(index: number) {
    this.editing.steps.splice(index, 1);
  }

  addCard() {
    this.editing.cards.push(new FlashcardCard());
  }

  removeCard(index: number) {
    this.editing.cards.splice(index, 1);
  }

  async save() {
    if (this.editing.gameType === 'match' && this.editing.pairs.filter(p => p.term && p.definition).length < 2) {
      this.toastr.error('Add at least two complete pairs.');
      return;
    }
    if (this.editing.gameType === 'sequence' && this.editing.steps.filter(s => s.text).length < 2) {
      this.toastr.error('Add at least two steps.');
      return;
    }
    if (this.editing.gameType === 'flashcard' && this.editing.cards.filter(c => c.front && c.back).length < 2) {
      this.toastr.error('Add at least two complete cards.');
      return;
    }
    this.saving = true;
    try {
      await this.classmanager.addeditlessongame(this.editing).toPromise();
      this.toastr.success('Activity saved');
      this.editing = null;
      await this.loadGames();
    } catch (e) {
      this.toastr.error('Could not save this activity');
    } finally {
      this.saving = false;
    }
  }

  confirmDelete(game: LessonGameEdit) {
    this.gameToDelete = game;
  }

  async deleteGame() {
    if (!this.gameToDelete) { return; }
    await this.classmanager.deletelessongame(this.gameToDelete.id, this.classid).toPromise();
    this.toastr.success('Activity deleted');
    this.gameToDelete = null;
    await this.loadGames();
  }

  goback() {
    this.router.navigate(['/user/lesson'], { queryParams: { classid: this.classid, moduleid: this.moduleinfo.id } });
  }
}
